import React, { useMemo } from "react";
import { Badge } from "../badge";
import { useVenue } from "../VenueProvider";
import { resumenPlanner } from "../../utils/comisiones";
import { formatDiaEvento } from "../../utils/dates";
import { fmtMoney } from "./shared";

// Cómo nos ha ido con este planner: cada evento que trajo, cuánto valió,
// cuánto se cobró, qué costó (con su comisión aparte) y qué dejó. Es lo que
// se quiere ver antes de aceptarle el siguiente. Solo lectura: los números
// salen de resumenPlanner (puro, con pruebas) y se editan en cada evento.

const pct = (v) => (v === null || v === undefined ? null : `${Math.round(v * 100)}%`);

const PlannerResultados = ({ proveedor, navigate }) => {
  const { events, payments, costos, calendarTimezone } = useVenue();
  const r = useMemo(
    () => resumenPlanner({ plannerId: proveedor?.id, events, payments, costos }),
    [proveedor?.id, events, payments, costos]
  );

  if (r.eventos.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-6 text-center text-sm text-zinc-400 italic">
        Todavía no ha traído ningún evento como planner.
      </div>
    );
  }

  const kpis = [
    { label: "Eventos", value: String(r.eventos.length) },
    { label: "Valor total", value: fmtMoney(r.valorTotal) },
    { label: "Pagado", value: fmtMoney(r.pagado), color: "text-emerald-600" },
    { label: "Debe", value: fmtMoney(r.debe), color: r.debe > 0 ? "text-amber-600" : "" },
    {
      label: "Comisión",
      value: fmtMoney(r.comisionTotal),
      hint: r.tasaEfectiva !== null ? `Tasa efectiva ${pct(r.tasaEfectiva)}` : null,
    },
    {
      label: "Utilidad",
      value: fmtMoney(r.utilidad),
      color: r.utilidad < 0 ? "text-red-600" : "text-emerald-600",
      hint: r.margen !== null ? `Margen ${pct(r.margen)} · costos ${fmtMoney(r.costosTotal)}` : null,
    },
  ];

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-zinc-100 dark:divide-zinc-700/40 text-center border-b border-zinc-100 dark:border-zinc-700/40">
        {kpis.map((k) => (
          <div key={k.label} className="px-3 py-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">{k.label}</div>
            <div className={`mt-0.5 text-sm font-bold text-zinc-900 dark:text-white ${k.color || ""}`}>{k.value}</div>
            {k.hint && <div className="text-[11px] text-zinc-400">{k.hint}</div>}
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs text-zinc-400">
              <th className="px-3 py-2 text-left font-medium">Evento</th>
              <th className="px-3 py-2 text-left font-medium">Fecha</th>
              <th className="px-3 py-2 text-right font-medium">Valor</th>
              <th className="px-3 py-2 text-right font-medium">Pagado</th>
              <th className="px-3 py-2 text-right font-medium">Saldo</th>
              <th className="px-3 py-2 text-right font-medium">Comisión</th>
              <th className="px-3 py-2 text-right font-medium">Costos</th>
              <th className="px-3 py-2 text-right font-medium">Utilidad</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
            {r.eventos.map((e) => (
              <tr key={e.eventId}>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/dashboard/eventos/${e.eventId}`, {
                        state: { from: "proveedor", proveedorId: proveedor.id, proveedorTab: "eventos" },
                      })
                    }
                    className="block max-w-[260px] truncate text-left font-medium text-zinc-900 dark:text-zinc-100 hover:underline"
                    title={e.nombre}
                  >
                    {e.nombre}
                  </button>
                  {e.cancelado && <Badge color="red">Cancelado</Badge>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-500">
                  {formatDiaEvento(e.startTime, calendarTimezone, { instante: true }) || "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{e.cancelado ? "—" : fmtMoney(e.valor)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-emerald-600">{e.pagado > 0 ? fmtMoney(e.pagado) : "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {e.saldo > 0 ? <span className="text-amber-600 font-medium">{fmtMoney(e.saldo)}</span> : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-zinc-600 dark:text-zinc-300">
                  {e.comision > 0 ? (
                    <span title={e.comisionEstado === "pagado" ? "Comisión pagada" : "Comisión por pagar"}>
                      {fmtMoney(e.comision)}
                      {e.comisionEstado !== "pagado" && <span className="ml-1 text-[10px] uppercase text-amber-600">por pagar</span>}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-zinc-600 dark:text-zinc-300">
                  {e.costos > 0 ? fmtMoney(e.costos) : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {e.utilidad === null ? (
                    <span className="text-zinc-300">—</span>
                  ) : (
                    <span
                      className={`font-medium ${e.utilidad < 0 ? "text-red-600" : "text-emerald-600"}`}
                      title={e.margen !== null ? `Margen ${pct(e.margen)}` : undefined}
                    >
                      {fmtMoney(e.utilidad)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {r.eventos.length > 1 && (
            <tfoot>
              <tr className="border-t border-zinc-200 dark:border-zinc-700/60 text-xs font-semibold">
                <td className="px-3 py-2 text-zinc-500" colSpan={2}>
                  Total · {r.eventos.length} eventos
                  {r.cancelados > 0 && (
                    <span className="font-normal text-zinc-400"> · {r.cancelados} cancelado{r.cancelados === 1 ? "" : "s"} fuera del margen</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-zinc-900 dark:text-white">{fmtMoney(r.valorTotal)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-emerald-600">{fmtMoney(r.pagado)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.debe > 0 ? <span className="text-amber-600">{fmtMoney(r.debe)}</span> : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-zinc-600 dark:text-zinc-300">{fmtMoney(r.comisionTotal)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-zinc-600 dark:text-zinc-300">{fmtMoney(r.costosTotal)}</td>
                <td className={`px-3 py-2 text-right whitespace-nowrap ${r.utilidad < 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {fmtMoney(r.utilidad)}
                  {r.margen !== null && <span className="ml-1 font-normal text-zinc-400">({pct(r.margen)})</span>}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="px-4 py-2 text-[11px] text-zinc-400 border-t border-zinc-100 dark:border-zinc-700/40">
        Costos incluye la comisión del planner. Un evento cancelado lista su gasto pero no entra al margen.
      </p>
    </div>
  );
};

export default PlannerResultados;
